import React, { useMemo } from "react";
import { StyleSheet } from "react-native";
import { DataTable, Surface, Text } from "react-native-paper";
import { WorkDataItem, formatEmployeeName, Order } from "./types";

interface WorkTableProps {
  orders: Order[];
  weekDates: Date[];
  showHalfSalary?: boolean;
}

const formatDate = (date: Date) => {
  return date.toLocaleDateString("ru-RU");
};

export const WorkTable: React.FC<WorkTableProps> = ({
  orders,
  weekDates,
  showHalfSalary,
}) => {
  // Преобразуем данные для таблицы работ
  const data: WorkDataItem[] = orders.flatMap((order) => {
    const date = order.dateCreated.toDate().toLocaleDateString("ru-RU");
    const mechanicsList = order.mechanics || [];
    return (
      mechanicsList
        // .filter((mechanic) => mechanic.fullName === "Иванов Владимир")
        .map((mechanic) => {
          // console.log("mechanic", mechanic);
          return {
            employeeId: mechanic.id,
            employeeName: mechanic.fullName,
            date,
            amount: mechanic.relatedObjectField.reduce(
              (sum: number, work) => sum + parseInt(work.price as string),
              0,
            ),
          };
        })
    );
  });

  // Получаем уникальных сотрудников
  const employees = useMemo(() => {
    return Array.from(new Set(data.map((item) => item.employeeName)));
  }, [data]);

  // Подсчет итогов по сотрудникам
  const employeeTotals = useMemo(() => {
    return employees.reduce(
      (acc, employee) => {
        acc[employee] = data
          .filter((item) => item.employeeName === employee)
          .reduce((sum, item) => sum + item.amount, 0);
        return acc;
      },
      {} as Record<string, number>,
    );
  }, [data, employees]);

  // Подсчет итогов по датам
  const dateTotals = useMemo(() => {
    return weekDates.reduce(
      (acc, date) => {
        const dateStr = formatDate(date);
        acc[dateStr] = data
          .filter((item) => item.date === dateStr)
          .reduce((sum, item) => sum + item.amount, 0);
        return acc;
      },
      {} as Record<string, number>,
    );
  }, [data, weekDates]);

  return (
    <Surface style={styles.container} elevation={1}>
      <Text variant="titleMedium" style={styles.tableTitle}>
        Работы
      </Text>
      <DataTable>
        <DataTable.Header>
          <DataTable.Title style={{ flex: 0.4 }}>Дата</DataTable.Title>
          {employees.map((employee) => (
            <DataTable.Title key={employee} numeric>
              {formatEmployeeName(employee)}
            </DataTable.Title>
          ))}
          <DataTable.Title numeric>Итого по дате</DataTable.Title>
        </DataTable.Header>

        {weekDates.map((date) => {
          const dateStr = formatDate(date);
          return (
            <DataTable.Row key={dateStr}>
              <DataTable.Cell style={{ flex: 0.4 }}>{dateStr}</DataTable.Cell>
              {employees.map((employee) => {
                const dayTotal = data
                  .filter(
                    (item) =>
                      item.date === dateStr && item.employeeName === employee,
                  )
                  .reduce((sum, item) => sum + item.amount, 0);

                return (
                  <DataTable.Cell key={`${dateStr}-${employee}`} numeric>
                    {dayTotal > 0 ? dayTotal.toLocaleString("ru-RU") : "-"}
                  </DataTable.Cell>
                );
              })}
              <DataTable.Cell numeric>
                {(dateTotals[dateStr] || 0).toLocaleString("ru-RU")}
              </DataTable.Cell>
            </DataTable.Row>
          );
        })}

        {showHalfSalary && (
          <DataTable.Row>
            <DataTable.Cell style={{ flex: 0.4 }}>ЗП</DataTable.Cell>
            {employees.map((employee) => (
              <DataTable.Cell key={`total-${employee}`} numeric>
                {((employeeTotals[employee] || 0) / 2).toLocaleString("ru-RU")}
              </DataTable.Cell>
            ))}
            <DataTable.Cell numeric>
              {(
                Object.values(employeeTotals).reduce(
                  (sum, total) => sum + (total || 0),
                  0,
                ) / 2
              ).toLocaleString("ru-RU")}
            </DataTable.Cell>
          </DataTable.Row>
        )}
        <DataTable.Row>
          <DataTable.Cell style={{ flex: 0.4 }}>Итог</DataTable.Cell>
          {employees.map((employee) => (
            <DataTable.Cell key={`total-${employee}`} numeric>
              {(employeeTotals[employee] || 0).toLocaleString("ru-RU")}
            </DataTable.Cell>
          ))}
          <DataTable.Cell numeric>
            {Object.values(employeeTotals)
              .reduce((sum, total) => sum + (total || 0), 0)
              .toLocaleString("ru-RU")}
          </DataTable.Cell>
        </DataTable.Row>
      </DataTable>
    </Surface>
  );
};

const styles = StyleSheet.create({
  container: {
    marginTop: 24,
    borderRadius: 8,
  },
  tableTitle: {
    padding: 16,
    paddingBottom: 8,
  },
});
